import { BACKEND_URL } from '@/lib/config'
import { getAuthToken } from '@/lib/supabase'

interface DialogResponse {
  success?: boolean
  name?: string
  error?: string
}

// Check if we're in an Office environment
function isOfficeEnvironment(): boolean {
  return (
    typeof Office !== 'undefined' &&
    Office.context?.ui?.displayDialogAsync !== undefined
  )
}

/**
 * Fetch the LinkedIn OAuth URL from the backend.
 * This is an authenticated call that returns the URL to open in a popup.
 */
async function getLinkedInOAuthUrl(
  permission: 'profile' | 'profile_post' = 'profile'
): Promise<string> {
  const token = await getAuthToken()
  if (!token) {
    throw new Error('Not authenticated')
  }

  if (!BACKEND_URL) {
    throw new Error('Backend URL not configured')
  }

  const response = await fetch(`${BACKEND_URL}/api/linkedin/oauth/url`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({ permission }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    let errorMessage = 'Failed to get OAuth URL'
    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.detail || errorMessage
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage)
  }

  const data = await response.json()
  return data.url
}

// Open LinkedIn OAuth for Office Add-ins (Excel)
async function openLinkedInAuthDialogOffice(
  permission: 'profile' | 'profile_post' = 'profile'
): Promise<{
  success: boolean
  name?: string
} | null> {
  const linkedInOAuthUrl = await getLinkedInOAuthUrl(permission)

  return new Promise((resolve, reject) => {
    let resolved = false
    let dialog: Office.DialogHandler

    const handler = (event: Office.DialogParentMessageReceivedEventArgs) => {
      if (!resolved) {
        resolved = true
        try {
          const message = JSON.parse(event.message) as DialogResponse

          if (message.error) {
            console.error('LinkedIn auth error from dialog:', message.error)
            dialog.close()
            setTimeout(() => reject(new Error(message.error)), 100)
          } else if (message.success && message.name) {
            dialog.close()
            const result = {
              success: true,
              name: message.name,
            }
            setTimeout(() => resolve(result), 100)
          } else {
            dialog.close()
            setTimeout(
              () => reject(new Error('Invalid response from LinkedIn auth')),
              100
            )
          }
        } catch (error) {
          console.error('Failed to parse LinkedIn auth response:', error)
          dialog.close()
          setTimeout(
            () => reject(new Error('Failed to parse LinkedIn auth response')),
            100
          )
        }
      }
    }

    console.log(
      'Opening LinkedIn auth dialog directly to LinkedIn:',
      linkedInOAuthUrl
    )

    Office.context.ui.displayDialogAsync(
      linkedInOAuthUrl,
      { height: 70, width: 40 },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          dialog = result.value
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            handler
          )

          dialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            (event) => {
              const errorOrMessage = event.error

              if (errorOrMessage === 12002) {
                if (!resolved) {
                  resolved = true
                  reject(new Error('LinkedIn auth dialog closed by user'))
                }
              } else if (errorOrMessage === 12006) {
                if (!resolved) {
                  resolved = true
                  reject(new Error('LinkedIn auth dialog navigation failed'))
                }
              }
            }
          )
        } else {
          reject(
            new Error(
              `Failed to open LinkedIn auth dialog: ${result.error?.message || result.status}`
            )
          )
        }
      }
    )
  })
}

// Open LinkedIn OAuth for Google Sheets (popup window)
async function openLinkedInAuthDialogPopup(
  permission: 'profile' | 'profile_post' = 'profile'
): Promise<{
  success: boolean
  name?: string
} | null> {
  const linkedInOAuthUrl = await getLinkedInOAuthUrl(permission)

  return new Promise((resolve, reject) => {
    // Clear any previous result
    localStorage.removeItem('linkedin_oauth_result')
    localStorage.removeItem('linkedin_oauth_timestamp')

    console.log(
      'Opening LinkedIn auth popup directly to LinkedIn:',
      linkedInOAuthUrl
    )

    const width = 500
    const height = 600
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2
    const popup = window.open(
      linkedInOAuthUrl,
      'linkedin_oauth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
    )

    if (!popup) {
      reject(new Error('Failed to open popup window. Please allow popups.'))
      return
    }

    let resolved = false

    // Listen for postMessage from callback page
    const messageHandler = (event: MessageEvent) => {
      if (event.data?.type === 'linkedin_oauth_result' && !resolved) {
        resolved = true
        window.removeEventListener('message', messageHandler)
        clearInterval(pollInterval)

        if (event.data.error) {
          reject(new Error(event.data.error))
        } else if (event.data.success && event.data.name) {
          resolve({ success: true, name: event.data.name })
        } else {
          reject(new Error('Invalid OAuth response'))
        }

        try {
          popup.close()
        } catch {
          // Ignore close errors
        }
      }
    }
    window.addEventListener('message', messageHandler)

    // Also poll localStorage as backup
    const pollInterval = setInterval(() => {
      if (resolved) {
        clearInterval(pollInterval)
        return
      }

      // Check if popup was closed
      if (popup.closed) {
        clearInterval(pollInterval)
        window.removeEventListener('message', messageHandler)

        // Check localStorage one more time
        const result = localStorage.getItem('linkedin_oauth_result')
        if (result && !resolved) {
          resolved = true
          try {
            const data = JSON.parse(result) as DialogResponse
            localStorage.removeItem('linkedin_oauth_result')
            localStorage.removeItem('linkedin_oauth_timestamp')

            if (data.error) {
              reject(new Error(data.error))
            } else if (data.success && data.name) {
              resolve({ success: true, name: data.name })
            } else {
              reject(new Error('Invalid OAuth response'))
            }
          } catch {
            reject(new Error('Failed to parse OAuth response'))
          }
        } else if (!resolved) {
          resolved = true
          reject(new Error('LinkedIn auth cancelled'))
        }
        return
      }

      // Check localStorage
      const result = localStorage.getItem('linkedin_oauth_result')
      const timestamp = localStorage.getItem('linkedin_oauth_timestamp')
      if (result && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10)
        if (age < 30000) {
          resolved = true
          clearInterval(pollInterval)
          window.removeEventListener('message', messageHandler)

          try {
            const data = JSON.parse(result) as DialogResponse
            localStorage.removeItem('linkedin_oauth_result')
            localStorage.removeItem('linkedin_oauth_timestamp')

            if (data.error) {
              reject(new Error(data.error))
            } else if (data.success && data.name) {
              resolve({ success: true, name: data.name })
            } else {
              reject(new Error('Invalid OAuth response'))
            }

            try {
              popup.close()
            } catch {
              // Ignore close errors
            }
          } catch {
            reject(new Error('Failed to parse OAuth response'))
          }
        }
      }
    }, 500)

    // Timeout after 5 minutes
    setTimeout(() => {
      if (!resolved) {
        resolved = true
        clearInterval(pollInterval)
        window.removeEventListener('message', messageHandler)
        try {
          popup.close()
        } catch {
          // Ignore close errors
        }
        reject(new Error('LinkedIn auth timeout'))
      }
    }, 5 * 60 * 1000)
  })
}

export async function openLinkedInAuthDialog(
  permission: 'profile' | 'profile_post' = 'profile'
): Promise<{
  success: boolean
  name?: string
} | null> {
  if (!BACKEND_URL) {
    throw new Error('Backend URL not configured')
  }

  if (isOfficeEnvironment()) {
    console.log('Using Office Dialog API for LinkedIn auth')
    return openLinkedInAuthDialogOffice(permission)
  } else {
    console.log('Using popup window for LinkedIn auth')
    return openLinkedInAuthDialogPopup(permission)
  }
}
